<template>
  <div fluid class="pa-4">
    <!-- Header -->
    <v-app-bar :color="getThemeColor" dark dense flat>
      <v-icon class="mr-2">mdi-package-variant</v-icon>
      <v-toolbar-title
        >{{ getCompanyLabels.products }} &
        {{ getCompanyLabels.services }} Management</v-toolbar-title
      >
      <v-spacer></v-spacer>
      <v-btn icon @click="showCreateDialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn icon @click="showFilters = !showFilters">
        <v-icon>mdi-filter-variant</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Quick Stats Cards -->
    <v-row dense class="px-3 py-2">
      <v-col cols="6" sm="3">
        <v-card :color="getThemeColor" dark>
          <v-card-text class="pa-3">
            <div class="text-h6">{{ totalProducts }}</div>
            <div class="caption">{{ getCompanyLabels.products }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card :color="getThemeColor" dark>
          <v-card-text class="pa-3">
            <div class="text-h6">{{ totalServices }}</div>
            <div class="caption">{{ getCompanyLabels.services }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card color="orange" dark>
          <v-card-text class="pa-3">
            <div class="text-h6">{{ lowStockCount }}</div>
            <div class="caption">Low Stock</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card color="green" dark>
          <v-card-text class="pa-3">
            <div class="text-h6">{{ onlineCount }}</div>
            <div class="caption">Online Store</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters & Quick Stats -->
    <v-expand-transition>
      <v-card v-show="showFilters" flat class="mb-2">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterType"
                :items="typeOptions"
                label="Item Type"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterSupplier"
                :items="suppliers"
                item-text="name"
                item-value="id"
                label="Supplier"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterCategory"
                :items="categories"
                item-text="name"
                item-value="id"
                label="Category"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterInventoryLevel"
                :items="inventoryLevelOptions"
                label="Inventory Level"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <!-- Main Content Tabs -->
    <v-tabs v-model="currentTab" :color="getThemeColor" show-arrows>
      <v-tab>All Items</v-tab>
      <v-tab>Products</v-tab>
      <v-tab>Services</v-tab>
      <v-tab>Pending Delivery</v-tab>
    </v-tabs>

    <!-- Search Bar -->
    <v-card flat class="pa-3">
      <v-text-field
        v-model="searchQuery"
        label="Search items..."
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
      ></v-text-field>
    </v-card>

    <!-- Items List -->
    <v-card flat>
      <v-data-table
        :headers="tableHeaders"
        :items="filteredItems"
        :search="searchQuery"
        :mobile-breakpoint="600"
        class="elevation-0"
        :loading="loading"
      >
        <template v-slot:item.images="{ item }">
          <v-avatar size="40" class="my-2">
            <v-img
              v-if="item.images && item.images.length > 0"
              :src="item.images[0]"
            ></v-img>
            <v-icon v-else>mdi-image-off</v-icon>
          </v-avatar>
        </template>

        <template v-slot:item.name="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.name }}</div>
            <div class="caption text--secondary">{{ item.code }}</div>
          </div>
        </template>

        <template v-slot:item.item_type="{ item }">
          <v-chip
            small
            :color="item.item_type === 'product' ? 'blue' : 'purple'"
            dark
          >
            {{ item.item_type }}
          </v-chip>
        </template>

        <template v-slot:item.inventory="{ item }">
          <v-chip small :color="getInventoryColor(item.inventory)" dark>
            {{ item.inventory || 0 }} units
          </v-chip>
        </template>

        <template v-slot:item.cost="{ item }">
          {{ formatCurrency(item.cost) }}
        </template>

        <template v-slot:item.price="{ item }">
          {{ formatCurrency(item.price) }}
        </template>

        <template v-slot:item.list_on_store="{ item }">
          <v-switch
            v-model="item.list_on_store"
            :color="getThemeColor"
            dense
            hide-details
            @change="toggleStoreStatus(item)"
          ></v-switch>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="manageItem(item)">
                <v-list-item-icon>
                  <v-icon small>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Manage</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewBarcode(item)">
                <v-list-item-icon>
                  <v-icon small>mdi-barcode</v-icon>
                </v-list-item-icon>
                <v-list-item-title>View Barcode</v-list-item-title>
              </v-list-item>
              <v-list-item @click="addToInvoice(item)">
                <v-list-item-icon>
                  <v-icon small>mdi-file-document</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Add to Invoice</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="item.item_type === 'product'"
                @click="markForDelivery(item)"
              >
                <v-list-item-icon>
                  <v-icon small>mdi-truck-delivery</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Mark for Delivery</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="deleteItem(item)" class="error--text">
                <v-list-item-icon>
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="900" scrollable persistent>
      <v-card>
        <v-card-title :class="getThemeColor + ' white--text'">
          <span>{{ editingItem ? "Edit Item" : "Create New Item" }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="itemForm" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.name"
                  label="Item Name"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.code"
                  label="Item Code/SKU"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.item_type"
                  :items="typeOptions"
                  label="Item Type"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.category_id"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  label="Category"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.supplier_id"
                  :items="suppliers"
                  item-text="name"
                  item-value="id"
                  label="Supplier/Partner"
                  outlined
                  dense
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <!-- <v-text-field
                  v-model.number="formData.emis"
                  label="EMIS Code"
                  outlined
                  dense
                ></v-text-field> -->
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="formData.cost"
                  label="Cost Price"
                  outlined
                  dense
                  type="number"
                  :prefix="getCurrency"
                  :rules="[rules.required, rules.positive]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="formData.price"
                  label="Selling Price"
                  outlined
                  dense
                  type="number"
                  :prefix="getCurrency"
                  :rules="[rules.required, rules.positive]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="formData.inventory"
                  label="Initial Stock"
                  outlined
                  dense
                  type="number"
                  :rules="[rules.positive]"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="formData.description"
                  label="Description"
                  outlined
                  dense
                  rows="3"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="formData.list_on_store"
                  label="List on Online Store"
                  :color="getThemeColor"
                  hide-details
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <div class="subtitle-2 mb-2">Product Images</div>
                <v-file-input
                  v-model="uploadedFiles"
                  label="Upload Images"
                  outlined
                  dense
                  multiple
                  accept="image/*"
                  prepend-icon="mdi-camera"
                  @change="handleFileUpload"
                  hide-details
                  class="mb-3"
                ></v-file-input>
                <v-row dense>
                  <v-col
                    v-for="(img, index) in formData.images"
                    :key="index"
                    cols="4"
                    sm="3"
                    md="2"
                  >
                    <v-card>
                      <v-img :src="img" aspect-ratio="1">
                        <v-btn
                          icon
                          small
                          color="error"
                          class="ma-1"
                          style="position: absolute; top: 0; right: 0"
                          @click="removeImage(index)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-img>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn
            :color="getThemeColor"
            dark
            :disabled="!formValid"
            @click="saveItem"
          >
            {{ editingItem ? "Update" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Item Dialog -->
    <v-dialog v-model="showManageDialog" max-width="1000" scrollable>
      <v-card v-if="selectedItem">
        <v-card-title :class="getThemeColor + ' white--text'">
          <span>Manage: {{ selectedItem.name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showManageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-tabs v-model="manageTab" :color="getThemeColor">
            <v-tab>Details</v-tab>
            <v-tab>Inventory</v-tab>
            <v-tab>Pricing</v-tab>
            <v-tab>Images</v-tab>
          </v-tabs>

          <v-tabs-items v-model="manageTab" class="mt-4">
            <!-- Details Tab -->
            <v-tab-item>
              <v-simple-table>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">Name:</td>
                    <td>{{ selectedItem.name }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Code:</td>
                    <td>{{ selectedItem.code }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Type:</td>
                    <td>
                      <v-chip
                        small
                        :color="
                          selectedItem.item_type === 'product'
                            ? 'blue'
                            : 'purple'
                        "
                        dark
                      >
                        {{ selectedItem.item_type }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Category:</td>
                    <td>{{ getCategoryName(selectedItem.category_id) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Supplier:</td>
                    <td>{{ getSupplierName(selectedItem.supplier_id) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">EMIS:</td>
                    <td>{{ selectedItem.emis || "N/A" }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Description:</td>
                    <td>{{ selectedItem.description || "No description" }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Online Store:</td>
                    <td>
                      <v-chip
                        small
                        :color="selectedItem.list_on_store ? 'green' : 'grey'"
                        dark
                      >
                        {{ selectedItem.list_on_store ? "Listed" : "Unlisted" }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <v-btn
                :color="getThemeColor"
                dark
                class="mt-4"
                @click="editItem(selectedItem)"
              >
                <v-icon left>mdi-pencil</v-icon>
                Edit Details
              </v-btn>
            </v-tab-item>

            <!-- Inventory Tab -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1"
                      >Current Inventory</v-card-title
                    >
                    <v-card-text>
                      <div
                        class="text-h4"
                        :style="{
                          color: getInventoryColor(selectedItem.inventory),
                        }"
                      >
                        {{ selectedItem.inventory || 0 }} units
                      </div>
                      <div class="caption mt-2">
                        Status: {{ getInventoryStatus(selectedItem.inventory) }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1">Adjust Stock</v-card-title>
                    <v-card-text>
                      <v-text-field
                        v-model.number="stockAdjustment"
                        label="Quantity"
                        outlined
                        dense
                        type="number"
                        hide-details
                      ></v-text-field>
                      <v-btn-toggle
                        v-model="stockOperation"
                        mandatory
                        class="mt-3"
                      >
                        <v-btn small value="add">Add Stock</v-btn>
                        <v-btn small value="remove">Remove Stock</v-btn>
                      </v-btn-toggle>
                      <v-btn
                        :color="getThemeColor"
                        dark
                        block
                        class="mt-3"
                        @click="adjustInventory"
                      >
                        Update Inventory
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Pricing Tab -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1"
                      >Current Pricing</v-card-title
                    >
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span class="font-weight-medium">Cost Price:</span>
                        <span>{{ formatCurrency(selectedItem.cost) }}</span>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <span class="font-weight-medium">Selling Price:</span>
                        <span>{{ formatCurrency(selectedItem.price) }}</span>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span class="font-weight-medium">Profit Margin:</span>
                        <span :style="{ color: getThemeColor }">
                          {{
                            calculateMargin(
                              selectedItem.cost,
                              selectedItem.price
                            )
                          }}%
                        </span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1"
                      >Update Pricing</v-card-title
                    >
                    <v-card-text>
                      <v-text-field
                        v-model.number="newCost"
                        label="New Cost Price"
                        outlined
                        dense
                        type="number"
                        :prefix="getCurrency"
                      ></v-text-field>
                      <v-text-field
                        v-model.number="newPrice"
                        label="New Selling Price"
                        outlined
                        dense
                        type="number"
                        :prefix="getCurrency"
                      ></v-text-field>
                      <v-btn
                        :color="getThemeColor"
                        dark
                        block
                        @click="updatePricing"
                      >
                        Update Prices
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Images Tab -->
            <v-tab-item>
              <v-file-input
                v-model="newImages"
                label="Add More Images"
                outlined
                dense
                multiple
                accept="image/*"
                prepend-icon="mdi-camera"
                @change="uploadAdditionalImages"
                class="mb-3"
              ></v-file-input>
              <v-row dense>
                <v-col
                  v-for="(img, index) in selectedItem.images"
                  :key="index"
                  cols="6"
                  sm="4"
                  md="3"
                >
                  <v-card>
                    <v-img :src="img" aspect-ratio="1">
                      <v-btn
                        icon
                        small
                        color="error"
                        class="ma-1"
                        style="position: absolute; top: 0; right: 0"
                        @click="removeItemImage(index)"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-img>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Barcode Dialog -->
    <v-dialog v-model="showBarcodeDialog" max-width="400">
      <v-card v-if="selectedItem">
        <v-card-title :class="getThemeColor + ' white--text'">
          Item Barcode
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showBarcodeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="text-center pa-6">
          <div class="text-h6 mb-4">{{ selectedItem.name }}</div>
          <div class="barcode-container mb-4">
            <svg :id="'barcode-' + selectedItem.id"></svg>
          </div>
          <div class="text-h6 mb-4">{{ selectedItem.code }}</div>
          <v-btn :color="getThemeColor" dark block @click="printBarcode">
            <v-icon left>mdi-printer</v-icon>
            Print Barcode
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add to Invoice Dialog -->
    <v-dialog v-model="showInvoiceDialog" max-width="500">
      <v-card>
        <v-card-title :class="getThemeColor + ' white--text'">
          Add to Invoice
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showInvoiceDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-select
            v-model="selectedInvoice"
            :items="invoices"
            item-text="invoice_number"
            item-value="id"
            label="Select Invoice"
            outlined
            dense
            :rules="[rules.required]"
          ></v-select>
          <v-text-field
            v-model.number="invoiceQuantity"
            label="Quantity"
            outlined
            dense
            type="number"
            :rules="[rules.required, rules.positive]"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showInvoiceDialog = false">Cancel</v-btn>
          <v-btn :color="getThemeColor" dark @click="confirmAddToInvoice">
            Add to Invoice
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import helpersJs from "@/plugins/helpers";
import productsAndServicesJs from "@/plugins/productsAndServices";
import invoicesAndQuotesJs from "@/plugins/invoicesAndQuotes";

export default {
  name: "ProductsServicesManager",

  data() {
    return {
      allProducts: [],
      allServices: [],
      loading: false,
      showFilters: true,
      currentTab: 0,
      manageTab: 0,
      searchQuery: "",

      // Filters
      filterType: null,
      filterSupplier: null,
      filterCategory: null,
      filterInventoryLevel: null,

      // Dialogs
      showCreateDialog: false,
      showManageDialog: false,
      showBarcodeDialog: false,
      showInvoiceDialog: false,

      // Form
      formValid: false,
      editingItem: null,
      uploadedFiles: [],
      formData: {
        name: "",
        description: "",
        code: "",
        category_id: null,
        supplier_id: null,
        list_on_store: false,
        item_type: "product",
        cost: 0,
        price: 0,
        emis: "",
        inventory: 0,
        images: [],
      },

      //Manage dialog data
      selectedItem: null,
      stockAdjustment: 0,
      stockOperation: "add",
      newCost: 0,
      newPrice: 0,
      newImages: [],

      // Invoice data
      selectedInvoice: null,
      invoiceQuantity: 1,

      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",

      // Options
      typeOptions: [
        { text: "Product", value: "product" },
        { text: "Service", value: "service" },
      ],

      inventoryLevelOptions: [
        { text: "In Stock", value: "in-stock" },
        { text: "Low Stock", value: "low-stock" },
        { text: "Out of Stock", value: "out-of-stock" },
      ],

      // Data
      items: [],
      categories: [],
      suppliers: [],
      invoices: [],

      // Validation rules
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "This field is required",
        positive: (v) =>
          v === null ||
          v === undefined ||
          v === "" ||
          v >= 0 ||
          "Must be a positive number",
      },

      // Table headers
      tableHeaders: [
        { text: "", value: "images", sortable: false, width: "60px" },
        { text: "Name", value: "name" },
        { text: "Type", value: "item_type", width: "100px" },
        { text: "Inventory", value: "inventory", width: "120px" },
        { text: "Cost", value: "cost", width: "100px" },
        { text: "Price", value: "price", width: "100px" },
        {
          text: "Online",
          value: "list_on_store",
          width: "80px",
          sortable: false,
        },
        { text: "Actions", value: "actions", sortable: false, width: "80px" },
      ],
    };
  },

  computed: {
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
    getCompanyLabels() {
      return this.$store.getters.getCompanyLabels;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },

    filteredItems() {
      let filtered = this.items;

      // Filter by tab
      if (this.currentTab === 1) {
        filtered = filtered.filter((item) => item.item_type === "product");
      } else if (this.currentTab === 2) {
        filtered = filtered.filter((item) => item.item_type === "service");
      } else if (this.currentTab === 3) {
        filtered = filtered.filter((item) => item.pending_delivery);
      }

      // Filter by type
      if (this.filterType) {
        filtered = filtered.filter(
          (item) => item.item_type === this.filterType
        );
      }

      // Filter by supplier
      if (this.filterSupplier) {
        filtered = filtered.filter(
          (item) => item.supplier_id === this.filterSupplier
        );
      }

      // Filter by category
      if (this.filterCategory) {
        filtered = filtered.filter(
          (item) => item.category_id === this.filterCategory
        );
      }

      // Filter by inventory level
      if (this.filterInventoryLevel) {
        if (this.filterInventoryLevel === "in-stock") {
          filtered = filtered.filter((item) => (item.inventory || 0) > 20);
        } else if (this.filterInventoryLevel === "low-stock") {
          filtered = filtered.filter(
            (item) => (item.inventory || 0) > 0 && (item.inventory || 0) <= 20
          );
        } else if (this.filterInventoryLevel === "out-of-stock") {
          filtered = filtered.filter((item) => (item.inventory || 0) === 0);
        }
      }

      return filtered;
    },

    totalProducts() {
      return this.items.filter((item) => item.item_type === "product").length;
    },

    totalServices() {
      return this.items.filter((item) => item.item_type === "service").length;
    },

    lowStockCount() {
      return this.items.filter(
        (item) => (item.inventory || 0) > 0 && (item.inventory || 0) <= 20
      ).length;
    },

    onlineCount() {
      return this.items.filter((item) => item.list_on_store).length;
    },
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        this.$store.commit("setLoader", true);
        // Simulate API calls
        await this.fetchItems();
        await this.fetchCategories();
        await this.fetchSuppliers();
        await this.fetchInvoices();
      } catch (error) {
        //console.log(error);
        this.showNotification("Error loading data", "error");
      } finally {
        this.$store.commit("setLoader", false);
        this.loading = false;
      }
    },
    stripCommasFromMoney(amount) {
      return helpersJs.stripCommasFromMoney(amount);
    },
    async getAllProducts() {
      const products = await productsAndServicesJs.getAllProducts();
      return products;
    },
    async getAllServices() {
      const services = await productsAndServicesJs.getAllServices();
      return services;
    },
    async getAllSuppliers() {
      return await productsAndServicesJs.getAllSuppliers();
    },
    async getAllServiceProviders() {
      return await productsAndServicesJs.getAllServiceProviders();
    },

    //Done
    async fetchItems() {
      this.allProducts = await this.getAllProducts();
      this.allServices = await this.getAllServices();
      this.items = [];

      this.allProducts.forEach((product) => {
        this.items.push({
          ...product,
          list_on_store:
            product.list_on_store &&
            (product.list_on_store.includes("es") ||
              product.list_on_store === true)
              ? true
              : false,
          inventory: Number(product.inventory.quantity_in_stock),
          pending_delivery: false,
        });
      });

      this.allServices.forEach((service) => {
        this.items.push({
          ...service,
          list_on_store:
            service.list_on_store &&
            (service.list_on_store.includes("es") ||
              service.list_on_store === true)
              ? true
              : false,
          inventory: service.quantity,
          pending_delivery: false,
        });
      });

      // Simulate API call with placeholder data
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     this.items = [
      //       {
      //         id: 1,
      //         name: "Dental Cleaning Service",
      //         description: "Professional teeth cleaning and polishing",
      //         code: "DS001",
      //         category_id: 1,
      //         supplier_id: 1,
      //         list_on_store: true,
      //         item_type: "service",
      //         cost: 150,
      //         price: 350,
      //         emis: "EMI001",
      //         inventory: 0,
      //         images: [
      //           "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 2,
      //         name: "Laptop - Dell XPS 15",
      //         description: "High-performance laptop for professionals",
      //         code: "LP001",
      //         category_id: 2,
      //         supplier_id: 2,
      //         list_on_store: true,
      //         item_type: "product",
      //         cost: 15000,
      //         price: 22000,
      //         emis: "EMI002",
      //         inventory: 15,
      //         images: [
      //           "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 3,
      //         name: "Legal Consultation",
      //         description: "One hour legal consultation",
      //         code: "LC001",
      //         category_id: 3,
      //         supplier_id: 3,
      //         list_on_store: true,
      //         item_type: "service",
      //         cost: 500,
      //         price: 1200,
      //         emis: "EMI003",
      //         inventory: 0,
      //         images: [
      //           "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 4,
      //         name: "Brake Pads Set",
      //         description: "Premium ceramic brake pads",
      //         code: "BP001",
      //         category_id: 4,
      //         supplier_id: 4,
      //         list_on_store: true,
      //         item_type: "product",
      //         cost: 800,
      //         price: 1500,
      //         emis: "EMI004",
      //         inventory: 8,
      //         images: [
      //           "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 5,
      //         name: "Prescription Glasses",
      //         description: "Designer frames with prescription lenses",
      //         code: "PG001",
      //         category_id: 5,
      //         supplier_id: 5,
      //         list_on_store: true,
      //         item_type: "product",
      //         cost: 600,
      //         price: 1800,
      //         emis: "EMI005",
      //         inventory: 25,
      //         images: [
      //           "https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 6,
      //         name: "Web Development Service",
      //         description: "Custom website development",
      //         code: "WD001",
      //         category_id: 6,
      //         supplier_id: 6,
      //         list_on_store: true,
      //         item_type: "service",
      //         cost: 5000,
      //         price: 15000,
      //         emis: "EMI006",
      //         inventory: 0,
      //         images: [
      //           "https://images.unsplash.com/photo-1547658719-da2b51169166?w=400",
      //         ],
      //         pending_delivery: false,
      //       },
      //       {
      //         id: 7,
      //         name: "Blood Pressure Monitor",
      //         description: "Digital automatic blood pressure monitor",
      //         code: "BP002",
      //         category_id: 7,
      //         supplier_id: 7,
      //         list_on_store: false,
      //         item_type: "product",
      //         cost: 300,
      //         price: 850,
      //         emis: "EMI007",
      //         inventory: 3,
      //         images: [
      //           "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=400",
      //         ],
      //         pending_delivery: true,
      //       },
      //     ];
      //     resolve();
      //   }, 500);
      // });
    },

    //Done
    async fetchCategories() {
      this.categories = await productsAndServicesJs.getAllCategories(
        this.getCurrentUser.emis
      );
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     this.categories = [
      //       { id: 1, name: "Dental Services" },
      //       { id: 2, name: "Electronics" },
      //       { id: 3, name: "Legal Services" },
      //       { id: 4, name: "Auto Parts" },
      //       { id: 5, name: "Optical" },
      //       { id: 6, name: "IT Services" },
      //       { id: 7, name: "Medical Equipment" },
      //       { id: 8, name: "Pharmaceuticals" },
      //       { id: 9, name: "Office Supplies" },
      //       { id: 10, name: "Consulting" },
      //     ];
      //     resolve();
      //   }, 300);
      // });
    },

    //Done
    async fetchSuppliers() {
      this.$store.commit("setLoader", true);
      this.suppliers = await this.getAllSuppliers();
      const servProviders = await this.getAllServiceProviders();
      this.suppliers.push([...servProviders]);
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     this.suppliers = [
      //       { id: 1, name: "SmileBright Dental Supplies" },
      //       { id: 2, name: "TechWorld Distributors" },
      //       { id: 3, name: "Legal Associates" },
      //       { id: 4, name: "AutoParts Direct" },
      //       { id: 5, name: "Vision Care Suppliers" },
      //       { id: 6, name: "Digital Solutions Inc" },
      //       { id: 7, name: "MedEquip International" },
      //     ];
      //     resolve();
      //   }, 300);
      // });
    },

    //Done
    async fetchInvoices() {
      const data = await invoicesAndQuotesJs.getInvoicesAndQuotesData();
      this.invoices = data.invoices;

      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     this.invoices = [
      //       { id: 1, invoice_number: "INV-2024-001" },
      //       { id: 2, invoice_number: "INV-2024-002" },
      //       { id: 3, invoice_number: "INV-2024-003" },
      //     ];
      //     resolve();
      //   }, 300);
      // });
    },

    //Done
    formatCurrency(amount) {
      if (amount === null || amount === undefined)
        return this.getCurrency + "0.00";
      return this.getCurrency + " " + parseFloat(amount).toFixed(2);
    },

    //Done
    getInventoryColor(inventory) {
      const stock = inventory || 0;
      if (stock === 0) return "red";
      if (stock <= 20) return "orange";
      return "green";
    },

    getInventoryStatus(inventory) {
      const stock = inventory || 0;
      if (stock === 0) return "Out of Stock";
      if (stock <= 20) return "Low Stock";
      return "In Stock";
    },

    getCategoryName(categoryId) {
      const category = this.categories.find((c) => c.id === categoryId);
      return category ? category.name : "N/A";
    },

    getSupplierName(supplierId) {
      const supplier = this.suppliers.find((s) => s.id === supplierId);
      return supplier ? supplier.name : "N/A";
    },

    calculateMargin(cost, price) {
      if (!cost || !price) return 0;
      return (((price - cost) / cost) * 100).toFixed(2);
    },

    handleFileUpload(files) {
      if (!files || files.length === 0) return;

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.formData.images.push(e.target.result);
        };
        reader.readAsDataURL(file);
      });
    },

    removeImage(index) {
      this.formData.images.splice(index, 1);
    },

    resetForm() {
      this.formData = {
        name: "",
        description: "",
        code: "",
        category_id: null,
        supplier_id: null,
        list_on_store: false,
        item_type: "product",
        cost: 0,
        price: 0,
        emis: "",
        inventory: 0,
        images: [],
      };
      this.uploadedFiles = [];
      this.editingItem = null;
      if (this.$refs.itemForm) {
        this.$refs.itemForm.resetValidation();
      }
    },

    closeDialog() {
      this.showCreateDialog = false;
      this.resetForm();
    },

    async saveItem() {
      if (!this.$refs.itemForm.validate()) return;

      this.loading = true;
      try {
        // Simulate API call

        if (this.editingItem) {
          await productsAndServicesJs.createOrUpdateProductOrService(
            this.editingItem
          );
          // Update existing item
          const index = this.items.findIndex(
            (i) => i.id === this.editingItem.id
          );
          if (index !== -1) {
            this.items.splice(index, 1, {
              ...this.editingItem,
              ...this.formData,
            });
          }
          this.showNotification("Item updated successfully", "success");
        } else {
          // Create new item
          const newItem = {
            ...this.formData,
            pending_delivery: false,
          };
          const response =
            await productsAndServicesJs.createOrUpdateProductOrService(newItem);
          if (response && response.status == "success") {
            this.items.unshift(response.item);
            this.showNotification("Item created successfully", "success");
          }
        }

        this.closeDialog();
      } catch (error) {
        this.showNotification("Error saving item", "error");
      } finally {
        this.loading = false;
      }
    },

    editItem(item) {
      this.editingItem = item;
      this.formData = { ...item };
      this.showManageDialog = false;
      this.showCreateDialog = true;
    },

    async deleteItem(item) {
      if (!confirm("Are you sure you want to delete this item?")) return;

      this.loading = true;
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const index = this.items.findIndex((i) => i.id === item.id);
        if (index !== -1) {
          this.items.splice(index, 1);
        }
        this.showNotification("Item deleted successfully", "success");
      } catch (error) {
        this.showNotification("Error deleting item", "error");
      } finally {
        this.loading = false;
      }
    },

    async toggleStoreStatus(item) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const status = item.list_on_store ? "listed on" : "removed from";
        this.showNotification("Item " + status + " online store", "success");
      } catch (error) {
        item.list_on_store = !item.list_on_store;
        this.showNotification("Error updating store status", "error");
      }
    },

    manageItem(item) {
      this.selectedItem = { ...item };
      this.newCost = item.cost;
      this.newPrice = item.price;
      this.showManageDialog = true;
    },

    async adjustInventory() {
      if (!this.stockAdjustment || this.stockAdjustment <= 0) {
        this.showNotification("Please enter a valid quantity", "warning");
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 300));

        const item = this.items.find((i) => i.id === this.selectedItem.id);
        if (item) {
          if (this.stockOperation === "add") {
            item.inventory = (item.inventory || 0) + this.stockAdjustment;
          } else {
            item.inventory = Math.max(
              0,
              (item.inventory || 0) - this.stockAdjustment
            );
          }
          this.selectedItem.inventory = item.inventory;
        }

        this.stockAdjustment = 0;
        this.showNotification("Inventory updated successfully", "success");
      } catch (error) {
        this.showNotification("Error updating inventory", "error");
      }
    },

    async updatePricing() {
      if (!this.newCost || !this.newPrice) {
        this.showNotification("Please enter valid prices", "warning");
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 300));

        const item = this.items.find((i) => i.id === this.selectedItem.id);
        if (item) {
          item.cost = this.newCost;
          item.price = this.newPrice;
          this.selectedItem.cost = this.newCost;
          this.selectedItem.price = this.newPrice;
        }

        this.showNotification("Pricing updated successfully", "success");
      } catch (error) {
        this.showNotification("Error updating pricing", "error");
      }
    },

    uploadAdditionalImages(files) {
      if (!files || files.length === 0) return;

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (!this.selectedItem.images) {
            this.selectedItem.images = [];
          }
          this.selectedItem.images.push(e.target.result);

          const item = this.items.find((i) => i.id === this.selectedItem.id);
          if (item) {
            if (!item.images) {
              item.images = [];
            }
            item.images.push(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      });

      this.newImages = [];
      this.showNotification("Images added successfully", "success");
    },

    removeItemImage(index) {
      this.selectedItem.images.splice(index, 1);
      const item = this.items.find((i) => i.id === this.selectedItem.id);
      if (item && item.images) {
        item.images.splice(index, 1);
      }
      this.showNotification("Image removed", "success");
    },

    viewBarcode(item) {
      this.selectedItem = item;
      this.showBarcodeDialog = true;
      this.$nextTick(() => {
        this.generateBarcode();
      });
    },

    generateBarcode() {
      // Simple barcode generation (in real app, use a library like JsBarcode)
      const svg = document.getElementById("barcode-" + this.selectedItem.id);
      if (svg) {
        svg.innerHTML =
          '<rect width="200" height="60" fill="white"/><text x="100" y="35" text-anchor="middle" font-family="monospace" font-size="12">||||| ' +
          this.selectedItem.code +
          " |||||</text>";
      }
    },

    printBarcode() {
      window.print();
      this.showNotification("Barcode sent to printer", "success");
    },

    addToInvoice(item) {
      this.selectedItem = item;
      this.invoiceQuantity = 1;
      this.selectedInvoice = null;
      this.showInvoiceDialog = true;
    },

    async confirmAddToInvoice() {
      if (!this.selectedInvoice || !this.invoiceQuantity) {
        this.showNotification("Please fill all fields", "warning");
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        this.showNotification("Item added to invoice successfully", "success");
        this.showInvoiceDialog = false;
      } catch (error) {
        this.showNotification("Error adding item to invoice", "error");
      }
    },

    async markForDelivery(item) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const itemToUpdate = this.items.find((i) => i.id === item.id);
        if (itemToUpdate) {
          itemToUpdate.pending_delivery = true;
        }
        this.showNotification("Item marked for delivery", "success");
      } catch (error) {
        this.showNotification("Error marking item for delivery", "error");
      }
    },

    showNotification(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.barcode-container {
  border: 1px dashed #ccc;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 4px;
}

.v-data-table >>> .v-data-table__mobile-row {
  min-height: 60px;
}

@media print {
  body * {
    visibility: hidden;
  }
  .barcode-container,
  .barcode-container * {
    visibility: visible;
  }
  .barcode-container {
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>
